import {
  Container,
  Title,
  Accordion,
  AccordionItem,
  AccordionControl,
  AccordionPanel,
} from "@mantine/core";
import classes from "./FAQ.module.css";
import mixpanel from "../tracking";
import { Link } from "wouter";

function TrackingAccordionItem({ children, value, ...props }) {
  return (
    <AccordionItem
      className={classes.item}
      onClick={() => {
        mixpanel.track("faq", { value });
      }}
      value={value}
      {...props}
    >
      {children}
    </AccordionItem>
  );
}
export default function HeroFaq() {
  return (
    <Container size="sm" className={classes.wrapper}>
      <Title ta="center" className={classes.title}>
        FAQ
      </Title>

      <Accordion variant="separated">
        <TrackingAccordionItem value="payment">
          <AccordionControl>How can I pay?</AccordionControl>
          <AccordionPanel>
            We support payment through the usual channels such as credit card
            via Stripe. Depending on your location, additional payment methods
            might be available.
          </AccordionPanel>
        </TrackingAccordionItem>

        <TrackingAccordionItem value="subscription-required">
          <AccordionControl>
            Do I need to create a subscription?
          </AccordionControl>
          <AccordionPanel>
            Not anymore. We have decided to adopt a simpler model where you can
            also{" "}
            <Link to="/payment/prepaid">buy credits on a one-time basis</Link>.
            However, you can still subscribe to a plan if you prefer.
          </AccordionPanel>
        </TrackingAccordionItem>

        <TrackingAccordionItem value="subscription-benefits">
          <AccordionControl>
            What are the benefits of a subscription?
          </AccordionControl>
          <AccordionPanel>
            Subscription credit prices are lower than one-time purchases.
          </AccordionPanel>
        </TrackingAccordionItem>

        <TrackingAccordionItem value="subscription-benefits">
          <AccordionControl>
            My payment was rejected or failed, what can I do?
          </AccordionControl>
          <AccordionPanel>
            In general, you can try a different payment method, like a different
            card or a different payment provider. The total amount can also play
            a role, especially if it&apos;s a large amount. Try buying a smaller
            tranche in this case. Alternatively, you can contact us at{" "}
            <a href="mailto:support@photovortex.com">
              mailto:support@photovortex.com
            </a>
            and we might find an alternative payment option.
          </AccordionPanel>
        </TrackingAccordionItem>

        <TrackingAccordionItem value="single-month">
          <AccordionControl>
            Can I subscribe just for a single month?
          </AccordionControl>
          <AccordionPanel>
            Sure! You can subscribe for a single month and cancel at any time.
            Your access will then end at the end of the current payment period.
            <br />
            <br />
            However, it&apos;s then easier to just buy{" "}
            <Link to="/payment/prepaid">a one-time package of credits</Link>.
          </AccordionPanel>
        </TrackingAccordionItem>

        <TrackingAccordionItem value="cancellation">
          <AccordionControl>How can I cancel my subscription?</AccordionControl>
          <AccordionPanel>
            You can cancel your subscription at any time. Just go to your
            profile page, choose &quot;Manage Subcription&quot; and cancel your plan. Your
            plan will then end at the end of the current payment period.
          </AccordionPanel>
        </TrackingAccordionItem>

        <TrackingAccordionItem value="refund">
          <AccordionControl>
            When I cancel my plan, can I get a refund of the credits I haven&apos;t
            used?
          </AccordionControl>
          <AccordionPanel>
            Unfortunately, that is not possible because of the overhead involved
            in that and the fees we have to pay for the payment provider.
          </AccordionPanel>
        </TrackingAccordionItem>

        <TrackingAccordionItem value="crypto-payments">
          <AccordionControl>Can I pay with crypto?</AccordionControl>
          <AccordionPanel>
            We don&apos;t support crypto payments at the moment, but we are
            considering it. Feel free to reach out to us to discuss!
          </AccordionPanel>
        </TrackingAccordionItem>

        <TrackingAccordionItem value="influencers">
          <AccordionControl>
            Does PhotoVortex allow creating AI influencers?
          </AccordionControl>
          <AccordionPanel>
            It does! Start by describing what your model is supposed to look
            like and generate a number of photos in different settings and
            sceneries. Preferably, use the same seed to ensure higher
            consistency. Once you have at least 10, better 20 photos, you can
            create a new AI character based on these photos. This will ensure
            that your AI influencer remains consistent even when using different
            styles, poses, clothes etc.
          </AccordionPanel>
        </TrackingAccordionItem>

        <TrackingAccordionItem value="video">
          <AccordionControl>
            Can I create videos with PhotoVortex?
          </AccordionControl>
          <AccordionPanel>
            Yes, absolutely! You can create videos by selecting an image and
            then selecting the &quot;video&quot; tab at the left. Through the
            corresponding prompt, you can instruct the AI to create a video
            based on the image. Videos will be 5s long, which currently is not
            configurable.
          </AccordionPanel>
        </TrackingAccordionItem>

        <TrackingAccordionItem value="register">
          <AccordionControl>How can I log-in / register?</AccordionControl>
          <AccordionPanel>
            We support log-in via Email (one-time login links) and via Google.
          </AccordionPanel>
        </TrackingAccordionItem>

        <TrackingAccordionItem value="guarantee">
          <AccordionControl>
            Are images guaranteed to look good?
          </AccordionControl>
          <AccordionPanel>
            Unfortunately, AI sometimes does strange things and is hard to
            control. Therefore, we can&apos;t guarantee things always work.
            Newer models generally have great adherence, but older models may
            not.
          </AccordionPanel>
        </TrackingAccordionItem>

        <TrackingAccordionItem value="customization">
          <AccordionControl>
            How can I customize the outcome of the images generated?
          </AccordionControl>
          <AccordionPanel>
            We provide a number of pre-defined styles that give the image a
            distinct tone. This could be a certain era, cinematic style, etc.
            Starting from the Pro version, you can also choose to use your own
            prompt to further customize the image.
          </AccordionPanel>
        </TrackingAccordionItem>

        <TrackingAccordionItem value="howitworks">
          <AccordionControl>How does PhotoVortex work?</AccordionControl>
          <AccordionPanel>
            You can upload selfies or other images of yourself into PhotoVortex
            and train our AI on you so it can generate AI photos of you in any
            style - photorealistic, anime, cartoon, pixel art, etc. Naturally,
            you can change the appearance of the generated images by changing
            the pose, action, environment, clothing etc. This way you can create
            a wide variety of images of yourself without having to pay a
            photographer or artist. After creating an account, you&apos;ll get
            instance access to PhotoVortex so you can create a photo shoot using
            your own model, create free-form images using our generator or alter
            existing images using our filters.
          </AccordionPanel>
        </TrackingAccordionItem>

        <TrackingAccordionItem value="types-of-photos">
          <AccordionControl>
            What type of photos do I need to train a model?
          </AccordionControl>
          <AccordionPanel>
            Ideally, upload photos taken from different angles, distances and
            backgrounds. Mix selfies, portraits and full-body shots. We
            recommend at least 10, better yet 20 photos for best results. Avoid
            uploading photos where your face is obstructed in any way. Also, do
            not upload group photos or photos with other people, because that
            might confuse the AI which face it is supposed to learn.
          </AccordionPanel>
        </TrackingAccordionItem>

        <TrackingAccordionItem value="time-training">
          <AccordionControl>
            How long does it take to train a model?
          </AccordionControl>
          <AccordionPanel>
            Training time is typically around 5-20 minutes, though even faster
            sometimes. The more photos you upload, the longer it will take, but
            the better the results will be.
          </AccordionPanel>
        </TrackingAccordionItem>

        <TrackingAccordionItem value="time-picture">
          <AccordionControl>
            How long does it take to take a picture?
          </AccordionControl>
          <AccordionPanel>
            Taking pictures is pretty fast and on the order of 10-20s per image.
            Note that you can queue up multiple images to be generated at once.
          </AccordionPanel>
        </TrackingAccordionItem>

        <TrackingAccordionItem value="price">
          <AccordionControl>What does PhotoVortex cost?</AccordionControl>
          <AccordionPanel>
            Please refer to the <a href="#pricing">pricing table</a>. You get 5
            months free if you pay yearly. You can cancel your plan at any time.
          </AccordionPanel>
        </TrackingAccordionItem>
      </Accordion>
    </Container>
  );
}
